import { runAuthPolicyHook } from '@xstudios/xapp-auth/services/authPolicyHooks'
import { useGoogleJwtAuthStore } from '@xstudios/xapp-auth/stores/googleJwtAuthStore'

import { createHttpClient } from '../shared/httpClients'

// Intentionally isolated clients per auth mode. Do not replace with shared
// singleton auth clients, otherwise interceptors from different auth modes can
// conflict when runtime-mode modules are loaded together.
export const api = createHttpClient('/api')
export const authApi = createHttpClient('/auth')
export const authTokenApi = createHttpClient('/auth/token')

let refreshTimer = null

export function cancelTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

export function isTokenExpired(token) {
  return secondsLeft(token) <= 0
}

export function scheduleTokenRefresh(token) {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!token) {
    console.debug('[auth/google] scheduleTokenRefresh skipped: no token')
    return
  }

  const timeLeft = secondsLeft(token)
  if (timeLeft <= 0) {
    console.debug('[auth/google] scheduleTokenRefresh skipped: token expired')
    return
  }

  const refreshBufferSeconds = getRefreshBufferSeconds(timeLeft)
  const refreshIn = Math.max(0, (timeLeft - refreshBufferSeconds) * 1000)
  console.debug('[auth/google] scheduleTokenRefresh', {
    refreshBufferSeconds,
    refreshInMs: refreshIn,
    timeLeftSeconds: timeLeft,
  })
  refreshTimer = setTimeout(async () => {
    try {
      console.debug('[auth/google] timer refresh firing')
      const authStore = useGoogleJwtAuthStore()
      await authStore.silentRefresh()
      console.debug('[auth/google] timer refresh finished')
    } catch (error) {
      console.error('Auto-refresh failed:', error)
    }
  }, refreshIn)
}

function applyRequestInterceptors(client) {
  client.interceptors.request.use(
    (config) => attachAuthHeader(config),
    (error) => Promise.reject(error)
  )
}

function applyResponseInterceptors(client) {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config
      try {
        const status = Number(error?.response?.status || 0)
        const requestUrl = String(originalRequest?.url || '')
        const isAuthTokenEndpoint = isGoogleTokenAuthEndpoint(requestUrl)

        if (
          status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !isAuthTokenEndpoint
        ) {
          console.debug(
            '[auth/google] 401 received, attempting silent refresh',
            {
              requestUrl,
              status,
            }
          )
          originalRequest._retry = true
          const authStore = useGoogleJwtAuthStore()
          const refreshed = await authStore.silentRefresh()
          if (refreshed && authStore.token) {
            console.debug(
              '[auth/google] 401 refresh succeeded, retrying request'
            )
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${authStore.token}`
            return client(originalRequest)
          }
          // authStore.silentRefresh() already fires onRefreshFailed when it
          // fails, so callers here don't need to re-dispatch it.
        }

        const detail = error?.response?.data?.detail
        const code = String(
          detail?.code || error?.response?.data?.code || ''
        ).trim()
        const message = String(
          detail?.message || detail || error?.response?.data?.message || ''
        ).toLowerCase()
        await runAuthPolicyHook('onRequestError', {
          code,
          detail,
          isAuthTokenEndpoint,
          message,
          requestUrl,
          status,
        })
      } catch (interceptorError) {
        console.error('Auth interceptor failed:', interceptorError)
      }
      throw error
    }
  )
}

async function attachAuthHeader(config) {
  const authStore = useGoogleJwtAuthStore()
  if (!authStore.bootstrapped) {
    await authStore.bootstrap()
  }
  if (authStore.token) {
    config.headers.Authorization = `Bearer ${authStore.token}`
  }
  return config
}

function getRefreshBufferSeconds(timeLeft) {
  if (timeLeft > 300) return 300
  return Math.max(15, Math.floor(timeLeft * 0.2))
}

function isGoogleTokenAuthEndpoint(url) {
  const value = String(url || '')
  return (
    value.includes('/auth/token/google') ||
    value.includes('/auth/token/refresh') ||
    value.includes('/auth/token/logout') ||
    value.includes('/auth/token/me') ||
    value.endsWith('/google') ||
    value.endsWith('/refresh') ||
    value.endsWith('/logout') ||
    value.endsWith('/me')
  )
}

function secondsLeft(jwt) {
  try {
    const [, payload] = jwt.split('.')
    const { exp } = JSON.parse(atob(payload))
    return exp - Math.floor(Date.now() / 1000)
  } catch {
    return 0
  }
}

applyRequestInterceptors(api)
applyRequestInterceptors(authApi)
// Important: do not attach auth header/bootstrap interceptor to auth token
// endpoints to avoid refresh/bootstrap recursion.

applyResponseInterceptors(api)
applyResponseInterceptors(authApi)
applyResponseInterceptors(authTokenApi)

export function login() {
  throw new Error('Manual login is disabled. Use Google OAuth login.')
}

export function loginWithGoogle(idToken) {
  return authTokenApi.post('/google', { id_token: idToken }).then((r) => r.data)
}

export function logout() {
  const authStore = useGoogleJwtAuthStore()
  return authTokenApi
    .post('/logout', { refresh_token: authStore.refreshToken || null })
    .then(() => true)
}

export function refresh(refreshToken) {
  return authTokenApi
    .post('/refresh', { refresh_token: refreshToken })
    .then((r) => r.data)
}

// The scheduled refresh timer is a single setTimeout fired when the token is
// issued. Background-tab throttling and OS/laptop sleep can delay or drop it
// entirely, so re-check the token whenever the tab becomes active again
// instead of relying solely on that one-shot timer.
function handleVisibilityRecovery() {
  const authStore = useGoogleJwtAuthStore()
  if (!authStore.token) {
    return
  }

  if (isTokenExpired(authStore.token)) {
    console.debug(
      '[auth/google] tab became active with an expired token, refreshing now'
    )
    void authStore.silentRefresh()
    return
  }

  // Re-arm the scheduler against the real clock in case the previous timer
  // was throttled or missed while the tab was inactive/asleep.
  scheduleTokenRefresh(authStore.token)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      handleVisibilityRecovery()
    }
  })
}

if (typeof window !== 'undefined') {
  window.addEventListener('focus', handleVisibilityRecovery)
}
